import React, { useState, useEffect, useMemo } from 'react'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import Spinner from '@atlaskit/spinner'
import Lozenge from '@atlaskit/lozenge'
import SearchIcon from '@atlaskit/icon/glyph/search'
import LabelIcon from '@atlaskit/icon/glyph/label'
import { fetchJiraLabels } from '../../utils/jiraContext'
import { bulkCreateLabels } from '../../api/testScenarioApi'
import { getActiveProjectId } from '../../constants/config'

/**
 * IMPORT LABELS MODAL
 *
 * Modal that fetches all available labels from the Jira instance and lets
 * the user select which ones to import into the project via the bulk create API.
 *
 * Features:
 * - Paginated fetch of all Jira labels on open
 * - Client-side search/filter
 * - Checkbox selection with Select All / Deselect All
 * - Already-imported labels shown as disabled with "Already imported" lozenge
 * - Bulk import via POST /labels/project/{projectId}/bulk
 */
function ImportLabelsModal({ isOpen, onClose, existingLabels = [], onImportComplete }) {
  // All Jira labels fetched from Jira API (string array)
  const [jiraLabels, setJiraLabels] = useState([])
  // Loading state for fetching Jira labels
  const [isLoadingLabels, setIsLoadingLabels] = useState(false)
  // Error state for fetch failure
  const [fetchError, setFetchError] = useState(null)
  // Search/filter query
  const [searchQuery, setSearchQuery] = useState('')
  // Selected label names (Set of strings)
  const [selectedLabels, setSelectedLabels] = useState(new Set())
  // Loading state for import operation
  const [isImporting, setIsImporting] = useState(false)

  /**
   * FETCH JIRA LABELS ON MODAL OPEN
   * Resets all state and fetches fresh label list each time the modal opens.
   */
  useEffect(() => {
    if (isOpen) {
      setIsLoadingLabels(true)
      setFetchError(null)
      setSearchQuery('')
      setSelectedLabels(new Set())
      setJiraLabels([])

      fetchJiraLabels()
        .then((labels) => {
          setJiraLabels(labels)
        })
        .catch(() => {
          setFetchError('Failed to load labels from Jira.')
        })
        .finally(() => {
          setIsLoadingLabels(false)
        })
    }
  }, [isOpen])

  /**
   * Set of existing label names (lowercased) for O(1) lookup.
   * Used to identify which Jira labels are already imported.
   */
  const existingLabelNames = useMemo(() => {
    return new Set(existingLabels.map(l => l.name.toLowerCase()))
  }, [existingLabels])

  /**
   * Filtered labels based on search query (client-side filter).
   */
  const displayLabels = useMemo(() => {
    if (!searchQuery.trim()) return jiraLabels
    const query = searchQuery.toLowerCase()
    return jiraLabels.filter(label => label.toLowerCase().includes(query))
  }, [jiraLabels, searchQuery])

  /**
   * Only selectable labels — those not already imported into the project.
   */
  const selectableLabels = useMemo(() => {
    return displayLabels.filter(label => !existingLabelNames.has(label.toLowerCase()))
  }, [displayLabels, existingLabelNames])

  // Check if all selectable (visible) labels are selected
  const allSelected = selectableLabels.length > 0 &&
    selectableLabels.every(label => selectedLabels.has(label))

  /**
   * Toggle a single label's selection state.
   * Ignores clicks on already-imported labels.
   */
  const handleToggleLabel = (labelName) => {
    if (existingLabelNames.has(labelName.toLowerCase())) return

    setSelectedLabels(prev => {
      const next = new Set(prev)
      if (next.has(labelName)) {
        next.delete(labelName)
      } else {
        next.add(labelName)
      }
      return next
    })
  }

  /**
   * Select All / Deselect All toggle.
   * Only affects selectable (non-existing) labels currently visible in the filtered list.
   */
  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all visible selectable labels
      setSelectedLabels(prev => {
        const next = new Set(prev)
        selectableLabels.forEach(label => next.delete(label))
        return next
      })
    } else {
      // Select all visible selectable labels
      setSelectedLabels(prev => {
        const next = new Set(prev)
        selectableLabels.forEach(label => next.add(label))
        return next
      })
    }
  }

  /**
   * IMPORT HANDLER
   * Sends selected label names to the bulk create API endpoint.
   * On success: shows notification, refreshes parent, closes modal.
   */
  const handleImport = async () => {
    if (selectedLabels.size === 0) return

    setIsImporting(true)
    try {
      await bulkCreateLabels(getActiveProjectId(), Array.from(selectedLabels))
      onImportComplete()
      onClose()
    } catch (error) {
      // Silent failure — no notification
    } finally {
      setIsImporting(false)
    }
  }

  /**
   * Close handler — prevents closing while import is in progress.
   */
  const handleClose = () => {
    if (!isImporting) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '520px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto'
          }}
        >
          {/* ── HEADER ── */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-brand-bold, #0052CC)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <LabelIcon
                label="Import"
                size="small"
                primaryColor="#FFFFFF"
              />
            </div>
            <div>
              <Dialog.Title
                style={{
                  fontSize: '20px',
                  fontWeight: 500,
                  color: 'var(--ds-text, #172B4D)',
                  margin: 0
                }}
              >
                Import Labels from Jira
              </Dialog.Title>
              {/* Show label count after loading */}
              {!isLoadingLabels && !fetchError && jiraLabels.length > 0 && (
                <p style={{
                  fontSize: '12px',
                  color: 'var(--ds-text-subtlest, #6B778C)',
                  margin: '2px 0 0 0'
                }}>
                  {jiraLabels.length} label{jiraLabels.length !== 1 ? 's' : ''} available in Jira
                </p>
              )}
            </div>
          </div>

          {/* ── BODY ── */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Search bar + Select All row */}
            {!isLoadingLabels && !fetchError && jiraLabels.length > 0 && (
              <div
                style={{
                  padding: '16px 24px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  borderBottom: '1px solid var(--ds-border, #DFE1E6)'
                }}
              >
                {/* Search input */}
                <div style={{ flex: 1 }}>
                  <TextField
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search labels..."
                    elemBeforeInput={
                      <div style={{ paddingLeft: '8px', display: 'flex', alignItems: 'center' }}>
                        <SearchIcon label="Search" size="small" primaryColor="var(--ds-text-subtlest, #6B778C)" />
                      </div>
                    }
                    isCompact
                  />
                </div>

                {/* Select All checkbox */}
                {selectableLabels.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = selectedLabels.size > 0 && !allSelected
                        }
                      }}
                      onChange={handleSelectAll}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        accentColor: 'var(--ds-background-brand-bold, #0052CC)'
                      }}
                    />
                    <span style={{
                      fontSize: '13px',
                      color: 'var(--ds-text-subtlest, #6B778C)',
                      whiteSpace: 'nowrap'
                    }}>
                      Select all ({selectableLabels.length})
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Label list (scrollable) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>
              {isLoadingLabels ? (
                /* Loading spinner */
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                  <Spinner size="medium" />
                </div>
              ) : fetchError ? (
                /* Error state */
                <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                  <p style={{
                    fontSize: '14px',
                    color: 'var(--ds-text-danger, #DE350B)',
                    margin: 0
                  }}>
                    {fetchError}
                  </p>
                  <Button
                    appearance="subtle-link"
                    onClick={() => {
                      // Retry fetch
                      setIsLoadingLabels(true)
                      setFetchError(null)
                      fetchJiraLabels()
                        .then(setJiraLabels)
                        .catch(() => setFetchError('Failed to load labels from Jira.'))
                        .finally(() => setIsLoadingLabels(false))
                    }}
                    style={{ marginTop: '8px' }}
                  >
                    Retry
                  </Button>
                </div>
              ) : displayLabels.length === 0 ? (
                /* Empty state */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '48px 20px',
                  gap: '8px'
                }}>
                  <LabelIcon label="" size="xlarge" primaryColor="var(--ds-icon-disabled, #A5ADBA)" />
                  <p style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--ds-text-subtle, #5E6C84)',
                    margin: 0
                  }}>
                    {searchQuery ? 'No labels match your search' : 'No labels found in Jira'}
                  </p>
                </div>
              ) : (
                /* Label list */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {displayLabels.map(label => {
                    const isExisting = existingLabelNames.has(label.toLowerCase())
                    const isSelected = selectedLabels.has(label)

                    return (
                      <div
                        key={label}
                        className="import-label-row"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          border: `1px solid ${isSelected ? 'var(--ds-border-selected, #4C9AFF)' : 'var(--ds-border, #DFE1E6)'}`,
                          backgroundColor: isExisting
                            ? 'var(--ds-background-disabled, #F4F5F7)'
                            : isSelected
                              ? 'var(--ds-background-selected, #E9F2FF)'
                              : 'var(--ds-surface, #FFFFFF)',
                          cursor: isExisting ? 'default' : 'pointer',
                          opacity: isExisting ? 0.7 : 1,
                          transition: 'all 0.15s ease'
                        }}
                        onClick={() => handleToggleLabel(label)}
                      >
                        {/* Checkbox */}
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginRight: '12px', display: 'flex', alignItems: 'center' }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected || isExisting}
                            disabled={isExisting}
                            onChange={() => handleToggleLabel(label)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: isExisting ? 'default' : 'pointer',
                              accentColor: 'var(--ds-background-brand-bold, #0052CC)'
                            }}
                          />
                        </div>

                        {/* Label name */}
                        <span style={{
                          flex: 1,
                          fontSize: '14px',
                          color: isExisting
                            ? 'var(--ds-text-disabled, #A5ADBA)'
                            : 'var(--ds-text, #172B4D)'
                        }}>
                          {label}
                        </span>

                        {/* "Already imported" indicator */}
                        {isExisting && (
                          <Lozenge appearance="default">Already imported</Lozenge>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── FOOTER ── */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {/* Selection count */}
            <span style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)'
            }}>
              {selectedLabels.size > 0
                ? `${selectedLabels.size} label${selectedLabels.size !== 1 ? 's' : ''} selected`
                : 'No labels selected'}
            </span>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                appearance="subtle"
                onClick={handleClose}
                isDisabled={isImporting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleImport}
                isLoading={isImporting}
                isDisabled={selectedLabels.size === 0}
              >
                Import{selectedLabels.size > 0 ? ` (${selectedLabels.size})` : ''}
              </Button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { ImportLabelsModal }
